import { useMutation, useQueryClient } from "@tanstack/react-query";
import fetchToBackend, { type BackendError } from "@/utils/fetchToBackend";
import { contractingKeys } from "@/lib/query-keys";
import { toastUtil } from "@/utils/toastUtil";
import { capture } from "@/lib/posthog";
import type { BidAnalysis } from "@/types/domain";

/**
 * Cruza los requisitos de una licitación contra los documentos de la empresa.
 * Lo dispara el usuario, y cada corrida reemplaza el análisis anterior.
 *
 * Tarda decenas de segundos: no lee el PDF, corre sobre los requisitos ya
 * extraídos y la ficha. Quien lo llame tiene que mostrar el estado de espera.
 */
export function useRunBidAnalysis() {
  const qc = useQueryClient();
  return useMutation<BidAnalysis, BackendError, { contracting_id: number }>({
    mutationFn: ({ contracting_id }) =>
      fetchToBackend<BidAnalysis>(
        `/contractings/${contracting_id}/bid-analysis`,
        { method: "POST" },
      ),
    onSuccess: (_data, { contracting_id }) => {
      capture("bid_analysis_run", { contracting_id });
      qc.invalidateQueries({ queryKey: contractingKeys.details() });
      // El badge de calificación del listado sale del mismo cruce.
      qc.invalidateQueries({ queryKey: contractingKeys.lists() });
      qc.invalidateQueries({ queryKey: contractingKeys.qualification() });
    },
    onError: (error) =>
      toastUtil(error.message || "No se pudo correr el cruce.", "error"),
  });
}
